import { Network } from '@puzzlehq/sdk';
import { trpcReact } from '@src/data/trpc';
import {
  VictoryLine,
  VictoryChart,
  VictoryAxis,
  VictoryTheme,
  VictoryArea,
  VictoryTooltip,
  VictoryScatter,
} from 'victory';
import { HStack, Spinner, Text, VStack } from 'ui_2';
import { useMemo, useState } from 'react';

interface GiveawayEntriesGraphProps {
  programId: string;
  giveawayId: string;
  network?: Network;
  height?: number;
  showUniqueEntrants?: boolean;
  showTotalEvents?: boolean;
}

export const GiveawayEntriesGraph = ({
  programId,
  giveawayId,
  network = Network.AleoMainnet,
  height = 300,
}: GiveawayEntriesGraphProps) => {
  // Internal state to control what data to show
  const [displayMode, setDisplayMode] = useState<'unique' | 'total'>('unique');

  // Internal state to control view type (cumulative vs rate)
  const [viewType, setViewType] = useState<'cumulative' | 'rate'>('cumulative');

  const { data, isLoading, error } =
    trpcReact.getCumulativeGiveawayData.useQuery({
      network,
      programId,
      giveawayId,
    });

  const chartData = useMemo(() => {
    if (!data?.cumulative || data.cumulative.length === 0) return [];

    const sourceData =
      viewType === 'cumulative' ? data.cumulative : data.derivative;

    return sourceData.map((point: any) => ({
      x: new Date(point.timestamp),
      y:
        viewType === 'cumulative'
          ? displayMode === 'unique'
            ? point.cumulativeUniqueEntrants
            : point.totalEvents
          : displayMode === 'unique'
          ? point.uniqueEntrantsPerHour
          : point.totalEventsPerHour,
    }));
  }, [data, displayMode, viewType]);

  const scatterData = useMemo(() => {
    if (!data?.cumulative || data.cumulative.length === 0) return [];

    const sourceData =
      viewType === 'cumulative' ? data.cumulative : data.derivative;

    return sourceData.map((point: any) => ({
      x: new Date(point.timestamp),
      y:
        viewType === 'cumulative'
          ? displayMode === 'unique'
            ? point.cumulativeUniqueEntrants
            : point.totalEvents
          : displayMode === 'unique'
          ? point.uniqueEntrantsPerHour
          : point.totalEventsPerHour,
      label: `${new Date(point.timestamp).toLocaleDateString()} at ${new Date(
        point.timestamp
      ).toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })}: ${
        viewType === 'cumulative'
          ? displayMode === 'unique'
            ? `${point.cumulativeUniqueEntrants} participants`
            : `${point.totalEvents} events`
          : displayMode === 'unique'
          ? `${point.uniqueEntrantsPerHour.toFixed(1)} participants/hour`
          : `${point.totalEventsPerHour.toFixed(1)} events/hour`
      }`,
    }));
  }, [data, displayMode, viewType]);

  if (isLoading) {
    return (
      <HStack className='w-full items-center justify-center p-8'>
        <Text font='medium-sm' color='gray-500'>
          Loading graph data...
        </Text>
        <Spinner />
      </HStack>
    );
  }

  if (error) {
    return (
      <VStack className='w-full items-center justify-center p-8'>
        <Text font='medium-sm' color='red'>
          Error loading graph data
        </Text>
        <Text font='regular-xs' color='gray-500'>
          {error.message}
        </Text>
      </VStack>
    );
  }

  if (!data?.cumulative || data.cumulative.length === 0) {
    return (
      <VStack className='w-full items-center justify-center p-8'>
        <Text font='medium-sm' color='gray-500'>
          No participation data available
        </Text>
      </VStack>
    );
  }

  const minDate = chartData[0]?.x;
  const maxDate = chartData[chartData.length - 1]?.x;

  // Calculate if duration is less than a day to determine label format
  const durationInHours =
    minDate && maxDate
      ? (maxDate.getTime() - minDate.getTime()) / (1000 * 60 * 60)
      : 0;
  const showHours = durationInHours <= 24;

  // Get current totals for display
  const currentData = data.cumulative[data.cumulative.length - 1];
  const maxRateData =
    viewType === 'rate'
      ? data.derivative.reduce((max: any, point: any) => {
          const currentRate =
            displayMode === 'unique'
              ? point.uniqueEntrantsPerHour
              : point.totalEventsPerHour;
          const maxRate =
            displayMode === 'unique'
              ? max.uniqueEntrantsPerHour
              : max.totalEventsPerHour;
          return currentRate > maxRate ? point : max;
        }, data.derivative[0])
      : null;

  return (
    <VStack className='w-full gap-2'>
      <div className='flex flex-col gap-2'>
        <div className='flex items-center justify-between'>
          <div className='flex flex-wrap gap-4 text-sm'>
            <Text font='medium-xs' color='gray-600'>
              <span className='font-semibold text-green-600'>
                {viewType === 'cumulative' ? 'Total: ' : 'Peak Rate: '}
              </span>
              {viewType === 'cumulative'
                ? displayMode === 'unique'
                  ? `${
                      currentData?.cumulativeUniqueEntrants || 0
                    } unique participants`
                  : `${currentData?.totalEvents || 0} total events`
                : maxRateData && displayMode === 'unique'
                ? `${maxRateData.uniqueEntrantsPerHour.toFixed(
                    1
                  )} participants/hour`
                : maxRateData
                ? `${maxRateData.totalEventsPerHour.toFixed(1)} events/hour`
                : 'N/A'}
            </Text>
            <Text font='medium-xs' color='gray-600'>
              <span className='font-semibold text-blue-600'>Duration: </span>
              {data.cumulative.length > 0 && minDate && maxDate
                ? showHours
                  ? `${Math.ceil(durationInHours)} hours`
                  : `${Math.ceil(
                      (maxDate.getTime() - minDate.getTime()) /
                        (1000 * 60 * 60 * 24)
                    )} days`
                : 'N/A'}
            </Text>
          </div>
          {/* View type toggle (Cumulative vs Rate) */}
          <div className='flex justify-end rounded-lg border border-gray-200 bg-gray-50 p-1'>
            <button
              onClick={() => setViewType('cumulative')}
              className={`rounded-md px-3 py-1 text-sm font-medium transition-all duration-200 ${
                viewType === 'cumulative'
                  ? 'border border-gray-200 bg-white text-gray-800 shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Cumulative
            </button>
            <button
              onClick={() => setViewType('rate')}
              className={`rounded-md px-3 py-1 text-sm font-medium transition-all duration-200 ${
                viewType === 'rate'
                  ? 'border border-gray-200 bg-white text-gray-800 shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Rate (per hour)
            </button>
          </div>
        </div>

        <div className='flex items-center justify-end'>
          {/* Data type toggle (Unique vs Total) */}
          <div className='flex rounded-lg border border-gray-200 bg-gray-50 p-1'>
            <button
              onClick={() => setDisplayMode('unique')}
              className={`rounded-md px-3 py-1 text-sm font-medium transition-all duration-200 ${
                displayMode === 'unique'
                  ? 'border border-green-200 bg-white text-green-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Unique Participants
            </button>
            <button
              onClick={() => setDisplayMode('total')}
              className={`rounded-md px-3 py-1 text-sm font-medium transition-all duration-200 ${
                displayMode === 'total'
                  ? 'border border-blue-200 bg-white text-blue-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Total Events
            </button>
          </div>
        </div>
      </div>

      <div className='w-full rounded-lg border border-gray-200 bg-white shadow-sm'>
        <VictoryChart
          key={`${viewType}-${displayMode}`}
          theme={VictoryTheme.material}
          height={height}
          padding={{ left: 100, top: 40, right: 60, bottom: 100 }}
          scale={{ x: 'time' }}
          width={800}
          domain={{
            x: minDate && maxDate ? [minDate, maxDate] : undefined,
          }}
        >
          <VictoryAxis
            dependentAxis
            tickFormat={(t) =>
              viewType === 'rate' ? `${t.toFixed(1)}` : `${Math.round(t)}`
            }
            style={{
              tickLabels: {
                fontSize: 12,
                fill: '#6B7280',
                fontFamily: 'system-ui, -apple-system, sans-serif',
              },
              grid: { stroke: '#E5E7EB', strokeWidth: 1 },
              axis: { stroke: '#D1D5DB', strokeWidth: 1 },
            }}
          />
          <VictoryAxis
            scale={{ x: 'time' }}
            tickCount={showHours ? 8 : 6}
            tickFormat={(t: any) => {
              const date = new Date(t);
              if (showHours) {
                // Show time for short durations (less than 24 hours)
                return date.toLocaleTimeString(undefined, {
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true,
                });
              } else {
                // Show date and time for longer durations
                const month = date.toLocaleDateString(undefined, {
                  month: 'short',
                });
                const day = date.getDate();
                const time = date.toLocaleTimeString(undefined, {
                  hour: 'numeric',
                  hour12: true,
                });
                return `${month} ${day}\n${time}`;
              }
            }}
            style={{
              tickLabels: {
                fontSize: 10,
                fill: '#6B7280',
                fontFamily: 'system-ui, -apple-system, sans-serif',
              },
              grid: { stroke: '#E5E7EB', strokeWidth: 1 },
              axis: { stroke: '#D1D5DB', strokeWidth: 1 },
            }}
          />

          {/* Area fill */}
          <VictoryArea
            key={`area-${viewType}-${displayMode}`}
            data={chartData}
            style={{
              data: {
                fill: displayMode === 'unique' ? '#10B981' : '#3B82F6',
                fillOpacity: 0.1,
                stroke: 'none',
              },
            }}
            animate={false}
          />

          {/* Line without labels */}
          <VictoryLine
            key={`line-${viewType}-${displayMode}`}
            data={chartData}
            style={{
              data: {
                stroke: displayMode === 'unique' ? '#10B981' : '#3B82F6',
                strokeWidth: 3,
                strokeLinecap: 'round',
              },
            }}
            animate={false}
          />

          {/* Invisible scatter points for hover tooltips */}
          <VictoryScatter
            key={`scatter-${viewType}-${displayMode}`}
            data={scatterData}
            size={8}
            style={{
              data: {
                fill: 'transparent',
                stroke: 'transparent',
              },
            }}
            labelComponent={
              <VictoryTooltip
                constrainToVisibleArea
                style={{
                  fill: '#1F2937',
                  fontSize: 12,
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                }}
                flyoutStyle={{
                  stroke: '#E5E7EB',
                  strokeWidth: 1,
                  fill: '#FFFFFF',
                  filter: 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1))',
                }}
                cornerRadius={6}
                pointerLength={8}
                renderInPortal={false}
              />
            }
          />
        </VictoryChart>
      </div>

      {/* Legend */}
      <div className='flex items-center gap-4 text-xs'>
        <div className='flex items-center gap-2'>
          <div
            className={`h-3 w-3 rounded-full ${
              displayMode === 'unique' ? 'bg-green-500' : 'bg-blue-500'
            }`}
          />
          <Text font='medium-xs' color='gray-600'>
            {viewType === 'cumulative'
              ? displayMode === 'unique'
                ? 'Unique Participants'
                : 'Total Events'
              : displayMode === 'unique'
              ? 'Participants/Hour'
              : 'Events/Hour'}
          </Text>
        </div>
      </div>
    </VStack>
  );
};
